import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft, SearchX } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';

export default function NotFoundPage() {
    return (
        <div className="min-h-screen flex flex-col bg-background text-foreground">
            <Navbar />
            <main className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-lg w-full text-center">
                    {/* Animated 404 Illustration */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 20 }}
                        className="relative mx-auto mb-10"
                    >
                        <div className="relative inline-flex items-center justify-center">
                            {/* Glowing backdrop */}
                            <div className="absolute inset-0 bg-primary/5 rounded-full blur-3xl scale-150" />

                            {/* 404 number */}
                            <div className="relative flex items-center gap-2">
                                <motion.span
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.1 }}
                                    className="text-8xl sm:text-9xl font-black tracking-tighter text-primary/20 select-none"
                                >
                                    4
                                </motion.span>

                                <motion.div
                                    initial={{ scale: 0, rotate: -180 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    transition={{ type: "spring", delay: 0.2, stiffness: 200 }}
                                    className="relative"
                                >
                                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-primary/15 to-primary/5 rounded-3xl flex items-center justify-center border border-primary/10 shadow-xl shadow-primary/5">
                                        <SearchX className="h-10 w-10 sm:h-12 sm:w-12 text-primary/60" strokeWidth={1.5} />
                                    </div>
                                </motion.div>

                                <motion.span
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="text-8xl sm:text-9xl font-black tracking-tighter text-primary/20 select-none"
                                >
                                    4
                                </motion.span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
                            Page not found
                        </h1>
                        <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto mb-10">
                            Looks like this page doesn't exist or has been moved.
                            Let's get you back on track.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                                asChild
                                className="rounded-full h-12 px-8 gap-2 font-semibold shadow-lg shadow-primary/20"
                            >
                                <Link to="/">
                                    <Home className="h-4 w-4" />
                                    Back to Home
                                </Link>
                            </Button>
                            <Button
                                variant="outline"
                                className="rounded-full h-12 px-8 gap-2 font-semibold"
                                onClick={() => window.history.back()}
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Go Back
                            </Button>
                        </div>
                    </motion.div>

                    {/* Decorative dots */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="mt-16 flex justify-center gap-1.5"
                    >
                        {[...Array(5)].map((_, i) => (
                            <motion.div
                                key={i}
                                animate={{ scale: [1, 1.3, 1] }}
                                transition={{
                                    repeat: Infinity,
                                    duration: 1.5,
                                    delay: i * 0.15,
                                    ease: "easeInOut",
                                }}
                                className="w-1.5 h-1.5 rounded-full bg-primary/20"
                            />
                        ))}
                    </motion.div>
                </div>
            </main>
        </div>
    );
}
